import {
  ComponentProps,
  RefAttributes,
  RefObject,
  useCallback,
  useEffect,
  useEffectEvent,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';

import { milliseconds, seconds, SingSetup, Song } from '~/interfaces';
import VideoPlayer, { VideoPlayerRef, VideoState } from '~/modules/elements/video-player/index';
import useKeyboard from '~/modules/hooks/use-keyboard';
import useKeyboardHelp from '~/modules/hooks/use-keyboard-help';
import usePrevious from '~/modules/hooks/use-previous';
import useToastsBesideLyrics from '~/modules/hooks/use-toasts-beside-lyrics';
import { PlayerNumber } from '~/modules/players/player-number';
import { FeatureFlags } from '~/modules/utils/feature-flags';
import useFeatureFlag from '~/modules/utils/use-feature-flag';
import usePauseOnLostInputs from '~/routes/game/singing/hooks/use-pause-on-lost-inputs';
import { useVideoPlayer } from '~/routes/game/singing/hooks/use-video-player';
import { cn } from '~/utils/cn';

import GameState from '../../../modules/game-engine/game-state/game-state';
import PauseMenu from './game-overlay/components/pause-menu';
import GameOverlay from './game-overlay/game-overlay';

interface Props extends Omit<ComponentProps<'div'>, 'ref'>, RefAttributes<PlayerRef> {
  singSetup: SingSetup;
  song: Song;
  width: number;
  height: number;
  autoplay?: boolean;
  showControls?: boolean;
  onCurrentTimeUpdate?: (newTime: milliseconds) => void;
  onSongEnd?: () => void;
  onStatusChange?: (status: VideoState) => void;

  effectsEnabled?: boolean;
  pauseMenu?: boolean;
  /**
   * Whether the pause menu is up. The readiness screen is a sibling of this component, not a child,
   * and its countdown has to hold while the menu is open - otherwise the song starts behind it.
   */
  onPauseMenuVisibilityChange?: (visible: boolean) => void;
  restartSong?: () => void;
  /** Online mode: only the host gets the skip-intro prompt. */
  skipIntroEnabled?: boolean;
  /** Online mode: routes the skip through the room server so it applies to everyone. */
  onSkipIntro?: (targetTimeSec: number) => void;
  /** Online mode: who leads the room, for the first-place medal — see GameOverlay's prop. */
  leadingPlayerNumber?: PlayerNumber | null;
  /**
   * Send the keyboard hints to the bottom strip instead of the corner box. The corner is fixed
   * top-right at z-1000 and the readiness screen puts its countdown there; that screen cannot move
   * the box itself, because this one mounts after it and owns the active entry.
   */
  hintsAsStrip?: boolean;
}

export interface PlayerRef {
  play: () => void;
  pause: () => void;
  seekTo: (time: number) => void;
  setPlaybackSpeed: (speed: number) => void;
  getCurrentTime: () => Promise<milliseconds>;
  getDuration?: () => seconds;
}

function usePlayerSetDuration(playerRef: RefObject<VideoPlayerRef | null>, currentStatus: VideoState) {
  const [duration, setDuration] = useState<seconds>(0);
  useEffect(() => {
    if (playerRef.current && currentStatus === VideoState.PLAYING && duration === 0) {
      playerRef.current.getDuration().then((dur: number) => {
        setDuration(dur);
        GameState.setDuration(dur);
      });
    }
  }, [duration, playerRef, currentStatus]);

  return duration;
}

function Player({
  song,
  width,
  height,
  autoplay = true,
  showControls = false,
  onCurrentTimeUpdate,
  onSongEnd,
  onStatusChange,
  effectsEnabled = true,
  singSetup,
  restartSong,
  pauseMenu = false,
  onPauseMenuVisibilityChange,
  skipIntroEnabled = true,
  onSkipIntro,
  leadingPlayerNumber,
  hintsAsStrip = false,
  ref,
  ...restProps
}: Props) {
  'use no memo'; // React Compiler: none of GameOverlay's props tick every frame (score comes from GameState, read at render time), so the compiler memoizes the <GameOverlay> element across renders and the score display never updates.
  const newVolumeFFEnabled = useFeatureFlag(FeatureFlags.NewVolume);
  const player = useRef<VideoPlayerRef | null>(null);
  const [pauseMenuVisible, setPauseMenuVisible] = useState(false);
  // Why the menu is up, when the room did not ask for it. Nothing else on screen says so: the
  // disconnect toast only fires once the phone has been silent long enough to be dropped, which is
  // a good ten seconds after the song stopped.
  const [pauseReason, setPauseReason] = useState<string | null>(null);

  const { className, ...containerProps } = restProps;

  const updateGameState = useCallback(
    (time: milliseconds) => {
      onCurrentTimeUpdate?.(time);
      GameState.setCurrentTime(time);
      GameState.update();
    },
    [onCurrentTimeUpdate],
  );

  const [currentStatus, setCurrentStatus] = useVideoPlayer(player, updateGameState);

  useEffect(() => {
    GameState.setSong(song);
    GameState.setSingSetup(singSetup);

    return () => {
      GameState.resetSingSetup();
    };
  }, [song, singSetup]);

  const duration = usePlayerSetDuration(player, currentStatus);

  useImperativeHandle(ref, () => ({
    seekTo: (time: seconds) => player.current!.seekTo(time),
    setPlaybackSpeed: (speed: number) => player.current!.setPlaybackSpeed(speed),
    play: () => player.current!.playVideo(),
    pause: () => player.current!.pauseVideo(),
    getCurrentTime: async () => ((await player.current?.getCurrentTime()) ?? 0) * 1000,
    getDuration: () => duration,
  }));

  const onStateChangeCallback = useCallback(
    (state: VideoState) => {
      setCurrentStatus(state);
      onStatusChange?.(state);
    },
    [setCurrentStatus, onStatusChange],
  );

  const isPauseMenuAvailable = pauseMenu;

  // The menu is reachable before the song has begun - the readiness screen sits over a player that
  // has not been told to play yet - and closing it must not be what starts the song. Tracked as
  // "has ever played" rather than read off `currentStatus`, because a player that was never
  // started reports its own state on the way out of `pauseVideo()` and is not worth trusting.
  const hasEverPlayed = useRef(false);
  if (currentStatus === VideoState.PLAYING) hasEverPlayed.current = true;

  const openPauseMenu = () => {
    setPauseMenuVisible(true);
    player.current?.pauseVideo();
  };

  const closePauseMenu = () => {
    setPauseMenuVisible(false);
    setPauseReason(null);
    if (hasEverPlayed.current) player.current?.playVideo();
  };

  const togglePauseMenu = () => {
    if (pauseMenuVisible) {
      closePauseMenu();
    } else {
      openPauseMenu();
    }
  };
  const reportVisibility = useEffectEvent((visible: boolean) => onPauseMenuVisibilityChange?.(visible));
  useEffect(() => {
    reportVisibility(pauseMenuVisible);
  }, [pauseMenuVisible]);

  const previousStatus = usePrevious(currentStatus);
  useEffect(() => {
    if (!isPauseMenuAvailable) return;

    const wasJustPaused =
      previousStatus !== VideoState.PAUSED &&
      previousStatus !== VideoState.BUFFERING &&
      currentStatus === VideoState.PAUSED;

    if (currentStatus === VideoState.PLAYING && pauseMenuVisible) {
      player.current?.pauseVideo();
    } else if (wasJustPaused && !pauseMenuVisible) {
      // Someone clicked on the video
      setPauseMenuVisible(true);
    }
  }, [pauseMenuVisible, currentStatus, previousStatus, isPauseMenuAvailable]);

  // Nobody left who can be heard: the song is playing to an empty room and scoring every note of
  // it as a miss, so it stops and the menu - which is where Restart lives - comes up.
  usePauseOnLostInputs(isPauseMenuAvailable && !pauseMenuVisible && currentStatus === VideoState.PLAYING, () => {
    setPauseReason('All microphones are disconnected. Reconnect one to carry on singing.');
    openPauseMenu();
  });

  useKeyboard(
    {
      back: togglePauseMenu,
    },
    isPauseMenuAvailable,
    [pauseMenuVisible],
  );

  const help = useMemo(
    () => ({
      back: 'Pause Menu',
      ...(hintsAsStrip ? { presentation: 'strip' as const } : {}),
    }),
    [hintsAsStrip],
  );
  useKeyboardHelp(help, effectsEnabled);
  useToastsBesideLyrics(effectsEnabled);
  return (
    <div className={cn('relative', className)} {...containerProps}>
      <PauseMenu
        onExit={onSongEnd}
        onResume={closePauseMenu}
        onRestart={restartSong!}
        open={pauseMenuVisible}
        reason={pauseReason}
      />
      {currentStatus !== VideoState.UNSTARTED && (
        <div
          className="pointer-events-none absolute inset-0 z-10 bg-black/20"
          style={{
            width: `${width}px`,
            height: `${height}px`,
          }}>
          <GameOverlay
            isPauseMenuVisible={pauseMenuVisible}
            onOpenPauseMenu={openPauseMenu}
            effectsEnabled={effectsEnabled}
            duration={duration}
            currentStatus={currentStatus}
            song={song}
            width={width}
            height={height}
            onSongEnd={onSongEnd}
            playerSetups={singSetup.players}
            videoPlayerRef={player}
            skipIntroEnabled={skipIntroEnabled}
            onSkipIntro={onSkipIntro}
            leadingPlayerNumber={leadingPlayerNumber}
          />
        </div>
      )}
      <div className="h-full overflow-hidden">
        <VideoPlayer
          ref={player}
          video={song.video}
          width={width}
          height={height}
          controls={showControls}
          autoplay={autoplay}
          disablekb={process.env.NODE_ENV !== 'development'}
          volume={newVolumeFFEnabled ? (song.volume ?? song.manualVolume) : song.manualVolume}
          startAt={song.videoGap ?? 0}
          onStateChange={onStateChangeCallback}
        />
        {currentStatus === VideoState.PLAYING && (
          <div
            className="absolute top-1/2 left-0 h-8 w-8 -translate-y-1/2 bg-black opacity-0"
            data-test="make-song-go-fast"
            onClick={() => {
              player.current?.setPlaybackSpeed(2);
            }}
          />
        )}
      </div>
    </div>
  );
}

export default Player;
